import { useEffect, useState } from "react";
import { makeStyles } from "tss-react/mui";
import { mdiChevronDown, mdiChevronUp } from "@mdi/js";
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Box,
  Tooltip,
  Typography,
  useTheme,
} from "@mui/material";
import { MiIcon } from "../../MiIcon/MiIcon";

/**
 * MiFilterAccordion component renders a customizable accordion with optional item count and tooltip.
 *
 * @param {Object} props - The component props.
 * @param {string} props.keyVal - The unique key for the accordion.
 * @param {string} props.title - The title of the accordion.
 * @param {boolean} props.disabled - If true, the accordion is disabled.
 * @param {Array} [props.selectedOptions=[]] - The list of selected options to display in the tooltip.
 * @param {string} [props.expandIconPath=ICONS_WEB.CHEVRON_DOWN] - The path to the icon used for expanding the accordion.
 * @param {boolean} [props.showItemCount=false] - If true, shows the count of selected options.
 * @param {React.ReactNode} props.children - The content to be displayed inside the accordion.
 *
 * @returns {JSX.Element} The rendered MiFilterAccordion component.
 */

export function MiFilterAccordion({
  keyVal,
  title,
  disabled,
  selectedOptions = [],
  showItemCount = false,
  children,
  selectedCategory,
}) {
  const [isExpanded, setIsExpanded] = useState(false);

  const { classes } = useStyles();
  const theme = useTheme();

  useEffect(() => {
    if (selectedOptions.length === 0) {
      setIsExpanded(false);
    }
  }, [selectedCategory]);

  const handleAccordionChange = (_, newExpanded) => {
    setIsExpanded(newExpanded);
  };

  return (
    <Box className={classes.root}>
      <Accordion
        key={keyVal}
        sx={{
          "&:before": {
            display: "none",
          },
        }}
        disabled={disabled}
        disableGutters
        square
        expanded={isExpanded}
        onChange={handleAccordionChange}
        // slotProps={{ heading: { component: "p" } }}
      >
        <AccordionSummary>
          <Box className={classes.accordionSummary}>
            <Typography
              sx={{
                fontWeight: theme.fontWeight.semiBold,
                maxWidth: 250,
              }}
            >
              {title}
            </Typography>
          </Box>
          <Box>
            {selectedOptions.length ? (
              <Tooltip
                title={selectedOptions.map((value) => (
                  <Typography
                    key={value}
                    variant="subtitle2"
                    sx={{ fontSize: theme.fontSize.small }}
                  >
                    {value}
                  </Typography>
                ))}
                placement="top-end"
              >
                <Box className={classes.overflowChip}>
                  <Typography
                    variant="subtitle2"
                    sx={{ color: theme.palette.primary.main }}
                  >
                    {showItemCount
                      ? selectedOptions.length
                      : selectedOptions[0]}
                  </Typography>
                </Box>
              </Tooltip>
            ) : null}
          </Box>
          {isExpanded ? (
            <MiIcon path={mdiChevronUp} />
          ) : (
            <MiIcon path={mdiChevronDown} />
          )}
        </AccordionSummary>
        <AccordionDetails>{children}</AccordionDetails>
      </Accordion>
    </Box>
  );
}

const useStyles = makeStyles()((theme) => ({
  root: {
    "& > .MuiPaper-root": {
      boxShadow: "none",
    },
    "& .Mui-disabled": {
      backgroundColor: `${theme.palette.common.white} !important`,
    },
    "& .MuiAccordionDetails-root": {
      padding: 0,
    },
    "& > * > * > .MuiAccordionSummary-root": {
      minHeight: 36,
      maxHeight: 36,
      borderBottom: `1px solid ${theme.palette.grey.accent}`,
      backgroundColor: theme.palette.grey.light,
      "& .MuiAccordionSummary-content > * > p": {
        fontSize: theme.fontSize.medium,
        whiteSpace: "nowrap",
        overflow: "hidden",
        textOverflow: "ellipsis",
      },
    },
  },
  accordionSummary: {
    paddingLeft: theme.spacing(1),
    display: "flex",
    flex: 1,
    alignItems: "center",
    justifyContent: "space-between",
  },
  overflowChip: {
    borderRadius: 4,
    padding: "0px 4px 0px 4px",
    backgroundColor: theme.palette.primary.light,
  },
}));
